// Path: src/pages/ResetPassword.js
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Alert,
  Link
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useModal } from '../../contexts/ModalContext';
import { useLoader } from '../../contexts/LoaderContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { openErrorModal, openSuccessModal } = useModal();
  const { showLoader, hideLoader } = useLoader();
  const navigate = useNavigate();

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate email
    if (!email) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    try {
      const response = await api.post('password_reset/', { 'email': email });
      hideLoader();
      if (response.status === 200) {
        if(response.data.redirect_url === 'account_status') {
          navigate("/auth/account-status/" + response.data.account_id); 
        } else {
          openSuccessModal(
            "Success",
            "Password reset link has been sent to your email address. Please check your inbox.",
            () => navigate("/auth/login")
          );
        }
        setEmail(''); // Clear the email field
      } else {
        openErrorModal("Error", response.data.message);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.email?.[0] ||
                          'Failed to process your request. Please try again.';
      openErrorModal("Error", errorMessage);
    } finally {
      hideLoader();
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        maxWidth: '450px',
        p: { xs: 3, sm: 4 },
        borderRadius: 2,
        backgroundColor: 'white',
      }}
    >
      <Typography 
        variant="h5" 
        component="h1" 
        align="center" 
        fontWeight="medium" 
        gutterBottom
      >
        Forgot Password
      </Typography>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        align="center" 
        sx={{ mb: 3 }}
      >
        Enter your email address and we'll send you a link to reset your password.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success ? (
        <Box sx={{ textAlign: 'center' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            Password reset link has been sent to your email address. Please check your inbox.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Didn't receive the email? Please check your spam folder or
          </Typography>
          <Button 
            onClick={() => {
              setSuccess(false);
              setError('');
            }}
            variant="text"
          >
            Try again
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!error}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ 
              py: 1,
              backgroundColor: '#007050',
              '&:hover': {
                backgroundColor: '#00513a',
              },
            }}
          >
            Send Reset Link
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link 
              component={RouterLink} 
              to="/auth/login" 
              variant="body2"
              sx={{
                color: '#007050',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Back to Login
            </Link>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ForgotPassword;